<template>
	<v-card class="pa-4" color="grey lighten-5" elevation="0">
		<v-alert type="info" v-if="!value.lastCheckin">
			This client has never checked in to a distribution.
		</v-alert>
		<v-alert type="warning" v-else-if="lastCheckinRecent" color="orange darken-4">
			This client checked in recently: {{moment(value.lastCheckin).format("dddd, MMMM Do YYYY, h:mm:ss a")}}
			<div><strong>This check-in was {{moment(value.lastCheckin).fromNow()}}</strong></div>
		</v-alert>
		<v-alert type="info" v-else>
			Last check-in date: {{moment(value.lastCheckin).format("dddd, MMMM Do YYYY, h:mm:ss a")}}
			<div><strong>This check-in was {{moment(value.lastCheckin).fromNow()}}</strong></div>
		</v-alert>
		<v-card class="mb-2">
			<v-avatar
				v-if="avatarUrl"
				class="float-right mr-2 mt-2"
			>
				<img
					:src="avatarUrl"
					alt="Gravatar"
				>
			</v-avatar>
			<v-container fluid>
				<v-row dense>
					<v-col :cols="12" lg="6" md="6" sm="6" xs="12">
						<div class="overline">Client:</div>
						<div class="caption">
							<data-string :value="value.clientCode" />
						</div>
						<div>
							<data-string :value="value.summary" />
						</div>
						<div>
							<data-string :value="value.emailAddress" />
						</div>
						<div>
							<data-string :value="value.phoneNumber" />
						</div>
					</v-col>
					<v-col :cols="12" lg="6" md="6" sm="6" xs="12">
						<div class="overline">Address:</div>
						<div>
							<div>{{value.streetAddress}}</div>
							<div v-if="value.unitNumber">{{value.unitNumber}}</div>
							<div>{{value.city}} {{value.state}} {{value.zip}}</div>
							<div>{{value.country}}</div>
						</div>
					</v-col>
					<v-col :cols="12" lg="6" md="6" sm="6" xs="12">
						<div class="overline">Preferred Location:</div>
						<div>
							<data-string :value="value.location.locationCode" />
						</div>
					</v-col>
					<v-col :cols="12" lg="6" md="6" sm="6" xs="12">
						<div class="overline">Language Spoken At Home:</div>
						<div>
							<data-string :value="value.language" />
						</div>
					</v-col>
				</v-row>
			</v-container>
		</v-card>
		<v-card class="mb-2">
			<v-container fluid>
				<v-row dense>
					<v-col 
						:cols="12" 
						lg="6"
						md="6"
						sm="6"
						xs="12"
					>
						<div class="overline"># Kids:</div>
						<div><data-integer :value="value.numberOfChildren" /></div>
					</v-col>
					<v-col 
						:cols="12" 
						lg="6"
						md="6"
						sm="6"
						xs="12"
					>
						<div class="overline"># Adults:</div>
						<div><data-integer :value="value.numberOfAdults" /></div>
					</v-col>
				</v-row>
			</v-container>
			<v-simple-table>
				<template v-slot:default>
					<thead>
						<tr>
							<th class="text-left">
								Child First Name
							</th>
							<th class="text-left">
								Child First Name
							</th>
							<th class="text-left">
								Child Gender
							</th>
							<th class="text-left">
								Child Age
							</th>
							<th class="text-left">
								School
							</th>
						</tr>
					</thead>
					<tbody>
						<tr
							v-for="child in value.children"
							:key="child.id"
						>
							<td>
								{{child.firstName}}
							</td>
							<td>
								{{child.lastName}}
							</td>
							<td>
								<v-chip small :color="genderColor(child.childGender)">{{child.childGender}}</v-chip>
							</td>
							<td>
								<v-chip small>{{age(child.childBirthday)}} years</v-chip>
							</td>
							<td>
								{{child.school.schoolCode}}
							</td>
						</tr>
					</tbody>
				</template>
			</v-simple-table>
		</v-card>
		<v-card class="mb-2" :loading="loaders.getEnrollments">
			<div class="pa-2">
				<div class="mb-2">Enrollment:</div>
				<v-card
					class="pa-2"
					v-for="enrollment in enrollments"
					:key="enrollment.id"
					dark
					color="blue-grey darken-2"
				>
					{{moment(enrollment.createdAt).format('YYYY')}} School Year
				</v-card>
				<v-alert v-if="enrollments.length === 0 && loaders.enrollmentLoaded" type="info" outlined dense>No enrollment found.</v-alert>
			</div>
		</v-card>
		<v-card class="mb-2" :loading="loaders.getCheckins">
			<div class="pa-2">
				<div class="mb-2">Distribution History (Check-Ins):</div>
				<v-card
					class="pa-2 mb-2"
					v-for="checkin in checkins"
					:key="checkin.id"
					color="teal lighten-4"
				>
					<div>
						{{checkin.distribution.distributionName}}
					</div>
					<div class="caption">Checked-in on {{moment(checkin.createdAt).format("dddd, MMMM Do YYYY, h:mm:ss a")}}</div>
				</v-card>
				<v-alert v-if="checkins.length === 0 && loaders.checkinsLoaded" type="info" outlined dense>There was no check-in history found.</v-alert>
			</div>
		</v-card>
		<v-card class="mb-2" :loading="loaders.getMappers">
			<div class="pa-2">
				<div class="mb-2">Cards Mapped:</div>
				<v-card
					class="pa-2 mb-2"
					v-for="mapper in mappers"
					:key="mapper.id"
					color="light-blue lighten-5"
				>
					<div>
						Card Code:
						<router-link :to="tools.linkToDetail({ namespace: 'people', pluralName: 'mappers', id: mapper.id })">{{mapper.cardCode}}</router-link>
					</div>
					<div class="caption">Mapped on {{moment(mapper.createdAt).format("dddd, MMMM Do YYYY, h:mm:ss a")}}</div>
				</v-card>
				<v-alert v-if="mappers.length === 0 && loaders.mappersLoaded" type="info" outlined dense>No mapped cards found.</v-alert>
			</div>
		</v-card>
	</v-card>
</template>

<script>
import apiService from '@/apiService';
import tools from '@/tools';
import moment from 'moment';
import DataString from '@/components/pieces/DataString';
import DataInteger from '@/components/pieces/DataInteger';
export default {
	components: {
		DataString,
		DataInteger
	},
	props: {
		routeInfo: {
			type: Object
		},
		value: {
			type: Object
		},
		isBase: {
			type: Boolean,
			default: true
		},
		headerColor: {
			type: String,
			default: 'primary'
		}
	},
	data() {
		return {
			loaders: {
				getMappers: false,
				getEnrollments: false,
				getCheckins: false,
				enrollmentLoaded: false,
				mappersLoaded: false,
				checkinsLoaded: false
			},
			mappers: [],
			enrollments: [],
			checkins: [],
			moment,
			tools
		}
	},
	computed: {
		lastCheckinRecent() {
			if (!this.value.lastCheckin) return false;
			const weekago = moment().subtract(1, 'weeks');
			const lcmoment = moment(this.value.lastCheckin);
			if (lcmoment.isAfter(weekago)) return true;
		},
		avatarUrl() {
			if (!this.value.emailAddress) return '';
			return `https://www.gravatar.com/avatar/${this.md5(this.value.emailAddress)}?d=mp`;
		}
	},
	methods: {
		genderColor(gender) {
			if (gender === 'Male') return 'light-blue lighten-5';
			if (gender === 'Female') return 'pink lighten-5';
			return 'yellow lighten-5';
		},
		age(birthday) {
			const m = moment(birthday);
			return Math.abs(m.diff(moment(), 'years'));
		},
		getMappers() {
			this.loaders.getMappers = true;
			apiService.list({
				namespace: 'people',
				pluralName: 'mappers',
				filters: {
					client: {
						clientCode: this.value.clientCode
					}
				}
			}, (err, response) => {
				this.loaders.getMappers = false;
				this.loaders.mappersLoaded = true;
				if (err) {
					this.$store.dispatch('app/applyObjectError', {
						show: true,
						text: err.message
					});
					return;
				}
				this.mappers = response;
			});
		},
		getCheckins() {
			this.loaders.getCheckins = true;
			apiService.list({
				namespace: 'people',
				pluralName: 'checkIns',
				filters: {
					client: {
						clientCode: this.value.clientCode
					}
				}
			}, (err, response) => {
				this.loaders.getCheckins = false;
				this.loaders.checkinsLoaded = true;
				if (err) {
					this.$store.dispatch('app/applyObjectError', {
						show: true,
						text: err.message
					});
					return;
				}
				this.checkins = response;
			});
		},
		getEnrollments() {
			this.loaders.getEnrollments = true;
			apiService.list({
				namespace: 'people',
				pluralName: 'enrollments',
				filters: {
					client: {
						clientCode: this.value.clientCode
					}
				}
			}, (err, response) => {
				this.loaders.getEnrollments = false;
				this.loaders.enrollmentLoaded = true;
				if (err) {
					this.$store.dispatch('app/applyObjectError', {
						show: true,
						text: err.message
					});
					return;
				}
				this.enrollments = response;
			});
		},
		md5(inputString) {
			var hc = "0123456789abcdef";

			function rh(n) { var j, s = ""; for (j = 0; j <= 3; j++) s += hc.charAt((n >> (j * 8 + 4)) & 0x0F) + hc.charAt((n >> (j * 8)) & 0x0F); return s; }

			function ad(x, y) { var l = (x & 0xFFFF) + (y & 0xFFFF); var m = (x >> 16) + (y >> 16) + (l >> 16); return (m << 16) | (l & 0xFFFF); }

			function rl(n, c) { return (n << c) | (n >>> (32 - c)); }

			function cm(q, a, b, x, s, t) { return ad(rl(ad(ad(a, q), ad(x, t)), s), b); }

			function ff(a, b, c, d, x, s, t) { return cm((b & c) | ((~b) & d), a, b, x, s, t); }

			function gg(a, b, c, d, x, s, t) { return cm((b & d) | (c & (~d)), a, b, x, s, t); }

			function hh(a, b, c, d, x, s, t) { return cm(b ^ c ^ d, a, b, x, s, t); }

			function ii(a, b, c, d, x, s, t) { return cm(c ^ (b | (~d)), a, b, x, s, t); }

			function sb(x) {
				var i;
				var nblk = ((x.length + 8) >> 6) + 1;
				var blks = new Array(nblk * 16);
				for (i = 0; i < nblk * 16; i++) blks[i] = 0;
				for (i = 0; i < x.length; i++) blks[i >> 2] |= x.charCodeAt(i) << ((i % 4) * 8);
				blks[i >> 2] |= 0x80 << ((i % 4) * 8);
				blks[nblk * 16 - 2] = x.length * 8;
				return blks;
			}
			var i, x = sb(inputString),
				a = 1732584193,
				b = -271733879,
				c = -1732584194,
				d = 271733878,
				olda, oldb, oldc, oldd;
			for (i = 0; i < x.length; i += 16) {
				olda = a;
				oldb = b;
				oldc = c;
				oldd = d;
				a = ff(a, b, c, d, x[i + 0], 7, -680876936);
				d = ff(d, a, b, c, x[i + 1], 12, -389564586);
				c = ff(c, d, a, b, x[i + 2], 17, 606105819);
				b = ff(b, c, d, a, x[i + 3], 22, -1044525330);
				a = ff(a, b, c, d, x[i + 4], 7, -176418897);
				d = ff(d, a, b, c, x[i + 5], 12, 1200080426);
				c = ff(c, d, a, b, x[i + 6], 17, -1473231341);
				b = ff(b, c, d, a, x[i + 7], 22, -45705983);
				a = ff(a, b, c, d, x[i + 8], 7, 1770035416);
				d = ff(d, a, b, c, x[i + 9], 12, -1958414417);
				c = ff(c, d, a, b, x[i + 10], 17, -42063);
				b = ff(b, c, d, a, x[i + 11], 22, -1990404162);
				a = ff(a, b, c, d, x[i + 12], 7, 1804603682);
				d = ff(d, a, b, c, x[i + 13], 12, -40341101);
				c = ff(c, d, a, b, x[i + 14], 17, -1502002290);
				b = ff(b, c, d, a, x[i + 15], 22, 1236535329);
				a = gg(a, b, c, d, x[i + 1], 5, -165796510);
				d = gg(d, a, b, c, x[i + 6], 9, -1069501632);
				c = gg(c, d, a, b, x[i + 11], 14, 643717713);
				b = gg(b, c, d, a, x[i + 0], 20, -373897302);
				a = gg(a, b, c, d, x[i + 5], 5, -701558691);
				d = gg(d, a, b, c, x[i + 10], 9, 38016083);
				c = gg(c, d, a, b, x[i + 15], 14, -660478335);
				b = gg(b, c, d, a, x[i + 4], 20, -405537848);
				a = gg(a, b, c, d, x[i + 9], 5, 568446438);
				d = gg(d, a, b, c, x[i + 14], 9, -1019803690);
				c = gg(c, d, a, b, x[i + 3], 14, -187363961);
				b = gg(b, c, d, a, x[i + 8], 20, 1163531501);
				a = gg(a, b, c, d, x[i + 13], 5, -1444681467);
				d = gg(d, a, b, c, x[i + 2], 9, -51403784);
				c = gg(c, d, a, b, x[i + 7], 14, 1735328473);
				b = gg(b, c, d, a, x[i + 12], 20, -1926607734);
				a = hh(a, b, c, d, x[i + 5], 4, -378558);
				d = hh(d, a, b, c, x[i + 8], 11, -2022574463);
				c = hh(c, d, a, b, x[i + 11], 16, 1839030562);
				b = hh(b, c, d, a, x[i + 14], 23, -35309556);
				a = hh(a, b, c, d, x[i + 1], 4, -1530992060);
				d = hh(d, a, b, c, x[i + 4], 11, 1272893353);
				c = hh(c, d, a, b, x[i + 7], 16, -155497632);
				b = hh(b, c, d, a, x[i + 10], 23, -1094730640);
				a = hh(a, b, c, d, x[i + 13], 4, 681279174);
				d = hh(d, a, b, c, x[i + 0], 11, -358537222);
				c = hh(c, d, a, b, x[i + 3], 16, -722521979);
				b = hh(b, c, d, a, x[i + 6], 23, 76029189);
				a = hh(a, b, c, d, x[i + 9], 4, -640364487);
				d = hh(d, a, b, c, x[i + 12], 11, -421815835);
				c = hh(c, d, a, b, x[i + 15], 16, 530742520);
				b = hh(b, c, d, a, x[i + 2], 23, -995338651);
				a = ii(a, b, c, d, x[i + 0], 6, -198630844);
				d = ii(d, a, b, c, x[i + 7], 10, 1126891415);
				c = ii(c, d, a, b, x[i + 14], 15, -1416354905);
				b = ii(b, c, d, a, x[i + 5], 21, -57434055);
				a = ii(a, b, c, d, x[i + 12], 6, 1700485571);
				d = ii(d, a, b, c, x[i + 3], 10, -1894986606);
				c = ii(c, d, a, b, x[i + 10], 15, -1051523);
				b = ii(b, c, d, a, x[i + 1], 21, -2054922799);
				a = ii(a, b, c, d, x[i + 8], 6, 1873313359);
				d = ii(d, a, b, c, x[i + 15], 10, -30611744);
				c = ii(c, d, a, b, x[i + 6], 15, -1560198380);
				b = ii(b, c, d, a, x[i + 13], 21, 1309151649);
				a = ii(a, b, c, d, x[i + 4], 6, -145523070);
				d = ii(d, a, b, c, x[i + 11], 10, -1120210379);
				c = ii(c, d, a, b, x[i + 2], 15, 718787259);
				b = ii(b, c, d, a, x[i + 9], 21, -343485551);
				a = ad(a, olda);
				b = ad(b, oldb);
				c = ad(c, oldc);
				d = ad(d, oldd);
			}
			return rh(a) + rh(b) + rh(c) + rh(d);
		}
	},
	created() {
		this.getMappers();
		this.getEnrollments();
		this.getCheckins();
	},
	activated() {
		this.getMappers();
		this.getEnrollments();
		this.getCheckins();
	}
}
</script>